"use client";

import {
    SPORT_NO_BG_COLOR,
    SPORT_NO_TEXT_COLOR,
    SPORT_YES_BG_COLOR,
    SPORT_YES_TEXT_COLOR,
} from "@/components/sport-event/sport-event-colors";
import { cn } from "@/lib/utils";
import { navigateToOrderGuessDetail, useAppRouter } from "@/utils/router";
import { formatMonetaryValue, formatUnitPrice, get, getOptionLabel } from "@/utils/utils";
import { BigMath, truncateOptionDesc } from "@yn/utils";
import { ArrowDownIcon, ArrowUpIcon } from "./ArrowIcons";
import { ChevronRightRedIcon } from "./chevron-right-red-icon";
import { toNumber } from "./utils";

export interface PositionItemLike {
    key: string;
    guessType: number;
    shares: number;
    price: number;
    currentPrice: number;
    profit: number;
    initialCost: number;
    [k: string]: any;
}

export interface OrderItemLike {
    orderNo?: string;
    betType?: number;
    guessType?: number;
    direction?: number;
    price?: number;
    totalQuantity?: number;
    totalDealtQuantity?: number;
    bestOppositePrice?: number;
    totalAmount?: number;
    [k: string]: any;
}

interface OrderPositionCardBaseProps {
    type: "position" | "order";
    item: PositionItemLike | OrderItemLike;
    currentGuess?: any;
    sportsDetail?: any;
    guessNo: string;
    eventNo: string;
    isSports?: boolean;
    optionLabelPrefix?: string;
}

interface OrderPositionCardPositionProps extends OrderPositionCardBaseProps {
    type: "position";
    item: PositionItemLike;
    onSell: ( item: PositionItemLike ) => void;
    onCancel?: never;
}

interface OrderPositionCardOrderProps extends OrderPositionCardBaseProps {
    type: "order";
    item: OrderItemLike;
    onCancel: ( order: OrderItemLike ) => void;
    onSell?: never;
}

export type OrderPositionCardProps = OrderPositionCardPositionProps | OrderPositionCardOrderProps;

const ChevronRedSvg = () => (
    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M5.03033 2.46967C4.73744 2.17678 4.26268 2.17678 3.96978 2.46967C3.67689 2.76256 3.67689 3.23732 3.96978 3.53022L6.43951 5.99994L3.96978 8.46967C3.67689 8.76256 3.67689 9.23732 3.96978 9.53022C4.26268 9.82311 4.73744 9.82311 5.03033 9.53022L8.03033 6.53022C8.32322 6.23732 8.32322 5.76256 8.03033 5.46967L5.03033 2.46967Z"
            fill="currentColor"
        />
    </svg>
);

export function OrderPositionCard( props: OrderPositionCardProps ) {
    const { type, item, currentGuess, guessNo, eventNo, isSports = false, sportsDetail, optionLabelPrefix } = props;
    const router = useAppRouter();

    const isYes = type === "position"
        ? ( item as PositionItemLike ).guessType === 1
        : ( ( item as OrderItemLike ).betType ?? ( item as OrderItemLike ).guessType ?? 1 ) === 1;

    const yesDesc = isSports && sportsDetail
        ? getOptionLabel( "yes", currentGuess, sportsDetail )
        : get( currentGuess, "yesDesc", "Yes" );
    const noDesc = isSports && sportsDetail
        ? getOptionLabel( "no", currentGuess, sportsDetail )
        : get( currentGuess, "noDesc", "No" );
    const optionLabel = truncateOptionDesc( isYes ? yesDesc : noDesc );

    const leftWidth = isSports ? "77px" : "86px";
    const leftBgStyle = isSports ? ( isYes ? SPORT_YES_BG_COLOR : SPORT_NO_BG_COLOR ) : undefined;
    const leftTextStyle = isSports ? ( isYes ? SPORT_YES_TEXT_COLOR : SPORT_NO_TEXT_COLOR ) : undefined;
    const guessGroupNo = get( currentGuess, "guessGroupNo", "" ) || get( item, "guessGroupNo", "" );
    const truncatedOptionLabelPrefix = optionLabelPrefix ? truncateOptionDesc( optionLabelPrefix ) : "";
    const optionLabelClassName = cn( "text-sm font-semibold", !isSports && ( isYes ? "text-yes" : "text-no" ) );
    const optionLabelStyle = leftTextStyle ? { color: leftTextStyle } : undefined;

    const renderOptionLabel = () => {
        if ( truncatedOptionLabelPrefix ) {
            return (
                <span className="flex max-w-full items-center gap-0.5 leading-tight" style={optionLabelStyle}>
                    <span className={optionLabelClassName}>{truncatedOptionLabelPrefix}</span>
                    <span className={optionLabelClassName}>{optionLabel}</span>
                </span>
            );
        }

        return <span className={optionLabelClassName} style={optionLabelStyle}>{optionLabel}</span>;
    };

    const handleCardClick = () => {
        if ( guessNo && eventNo ) {
            navigateToOrderGuessDetail( router, { eventNo, guessGroupNo, guessNo } );
        }
    };

    const cardRootClass = cn(
        "relative bg-surface-card rounded-lg overflow-hidden border border-stroke flex items-center pr-3 h-[54px] cursor-pointer",
    );

    if ( type === "position" ) {
        const pos = item as PositionItemLike;
        const currentValue = BigMath.times( pos.shares, pos.currentPrice );
        const initialCost = pos.initialCost;

        const trend = BigMath.gt( currentValue, initialCost )
            ? "up"
            : BigMath.lt( currentValue, initialCost )
            ? "down"
            : "unchanged";

        return (
            <div
                role="button"
                tabIndex={0}
                className={cardRootClass}
                onClick={handleCardClick}
                onKeyDown={( e ) => e.key === "Enter" && handleCardClick()}
            >
                {/* Left: desc only */}
                <div
                    className={cn(
                        "h-full flex flex-col items-center justify-center flex-shrink-0",
                        !isSports && ( isYes ? "bg-yes-subtle" : "bg-no-subtle" ),
                    )}
                    style={{ width: leftWidth, ...( leftBgStyle ? { backgroundColor: leftBgStyle } : {} ) }}
                >
                    {renderOptionLabel()}
                </div>

                {/* Middle: Invested + Current */}
                <div className="flex-1 flex items-center gap-2 pl-3 min-w-0">
                    <div className="flex flex-col h-9 justify-center w-[63px] shrink-0">
                        <div className="text-sm font-semibold text-t1">
                            {formatMonetaryValue( pos.initialCost, { precision: 2, scaleMinAmount: 1000 } )}
                        </div>
                        <div className="text-xs font-medium text-t3">Invested</div>
                    </div>
                    <div className="flex-1 flex flex-col h-9 justify-center min-w-0">
                        <div className="flex items-center gap-1">
                            <span
                                className={cn(
                                    "text-sm font-semibold",
                                    trend === "up" && "text-action-accent",
                                    trend === "down" && "text-no",
                                    trend === "unchanged" && "text-t1",
                                )}
                            >
                                {formatMonetaryValue( currentValue, { precision: 2, scaleMinAmount: 1000 } )}
                            </span>
                            {trend === "up" && <ArrowUpIcon className="size-3.5 flex-shrink-0 text-action-accent" />}
                            {trend === "down" && <ArrowDownIcon className="size-3.5 flex-shrink-0 text-no" />}
                        </div>
                        <div className="text-xs font-medium text-t3">Current</div>
                    </div>
                </div>

                {/* Right: Sell */}
                <button
                    type="button"
                    onClick={( e ) => {
                        e.stopPropagation();
                        props.onSell( pos );
                    }}
                    className="text-state-danger text-sm font-medium shrink-0 flex items-center gap-0.5 capitalize"
                >
                    Sell
                    <span className="w-3 h-3 flex-shrink-0">
                        <ChevronRightRedIcon />
                    </span>
                </button>
            </div>
        );
    }

    // type === "order"
    const order = item as OrderItemLike;
    const isBuy = toNumber( get( order, "direction", 1 ), 1 ) === 1;
    const badgePrice = get( order, "bestOppositePrice", order.price ) ?? order.price ?? 0;
    const price = toNumber( get( order, "price", 0 ), 0 );

    const totalQuantity = toNumber( get( order, "totalQuantity", 0 ) );
    const totalDealtQuantity = toNumber( get( order, "totalDealtQuantity", 0 ) );
    const reminderQuantity = BigMath.minus( totalQuantity, totalDealtQuantity );

    const initialCost = BigMath.times( price, reminderQuantity );
    const marketPrice = toNumber( get( order, "marketPrice", 0 ), 0 );
    const currentValue = BigMath.times( marketPrice, reminderQuantity );
    const trend = BigMath.gt( currentValue, initialCost )
        ? "up"
        : BigMath.lt( currentValue, initialCost )
        ? "down"
        : "unchanged";

    return (
        <div
            role="button"
            tabIndex={0}
            className={cardRootClass}
            onClick={handleCardClick}
            onKeyDown={( e ) => e.key === "Enter" && handleCardClick()}
        >
            {/* Left: desc only */}
            <div
                className={cn(
                    "h-full flex flex-col items-center justify-center flex-shrink-0",
                    !isSports && ( isYes ? "bg-yes-subtle" : "bg-no-subtle" ),
                )}
                style={{ width: leftWidth, ...( leftBgStyle ? { backgroundColor: leftBgStyle } : {} ) }}
            >
                {renderOptionLabel()}
                <div className="text-t3 text-xs font-medium">
                    {isBuy ? "Buy" : "Sell"} @ {formatUnitPrice( badgePrice, { precision: 1, scaleMinAmount: 1000 } )}
                </div>
            </div>

            {/* Middle: Invested (price * totalQuantity) + Current Value (totalAmount) */}
            <div className="flex-1 flex items-center gap-2 pl-3 min-w-0">
                <div className="flex flex-col gap-1 h-9 justify-center w-[63px] shrink-0">
                    <div className="text-sm font-semibold text-t1">
                        {formatMonetaryValue( initialCost, { precision: 2, scaleMinAmount: 1000 } )}
                    </div>
                    <div className="text-xs font-medium text-t3">Invested</div>
                </div>
                <div className="flex-1 flex flex-col gap-1 h-9 justify-center min-w-0">
                    <div
                        className={cn(
                            "flex items-center gap-1",
                            trend === "up" && "text-action-accent",
                            trend === "down" && "text-no",
                            trend === "unchanged" && "text-t1",
                        )}
                    >
                        <span className="text-sm font-semibold">
                            {formatMonetaryValue( currentValue, { precision: 2, scaleMinAmount: 1000 } )}
                        </span>
                        {trend === "up" && (
                            <ArrowUpIcon className="size-3.5 flex items-center justify-center flex-shrink-0" />
                        )}
                        {trend === "down" && (
                            <ArrowDownIcon className="size-3.5 flex items-center justify-center flex-shrink-0" />
                        )}
                    </div>
                    <div className="text-xs font-medium text-t3">Current</div>
                </div>
            </div>

            {/* Right: Cancel + chevron */}
            <button
                type="button"
                onClick={( e ) => {
                    e.stopPropagation();
                    props.onCancel( order );
                }}
                className="text-state-danger text-sm font-medium shrink-0 flex items-center gap-0.5 capitalize"
            >
                Cancel
                <ChevronRedSvg />
            </button>
        </div>
    );
}
